import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Divider,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Text,
} from "@mantine/core";
import { IconDeviceMobile } from "@tabler/icons-react";
import {
  PREVIEW_FRAME_SOURCE,
  PREVIEW_HOST_SOURCE,
  PREVIEW_VIEW,
} from "@congrs/schemas";

import { webConfig } from "../../../../config";
import { useGetAllFeatureFlags } from "../../../api/featureFlags";
import { getAccessToken } from "../../../services/authStore";

// An iPhone 15/16 viewport, scaled down to fit beside the settings, so the app
// lays itself out against the widths it actually ships on. The frame draws that
// phone's status bar and Dynamic Island over the app
// (`components/preview/PreviewDevice.tsx` in mobile-next), so a change of model
// has to happen on both sides.
const PHONE_WIDTH = 393;
const PHONE_HEIGHT = 852;
const PHONE_CORNER_RADIUS = 55;
const SCALE = 0.78;

// Long enough that a slow first bundle isn't reported as a failure.
const READY_TIMEOUT_MS = 8000;

// A single drag of the speaker count posts a message per step; the frame only
// needs the one the organizer settled on.
const CONFIG_DEBOUNCE_MS = 200;

const previewOrigin = new URL(webConfig.mobileWebUrl, window.location.origin)
  .origin;

/**
 * Live preview of the attendee app, driven by the unsaved event config draft.
 * The frame is the mobile app's own web build (`/preview` in mobile-next), so
 * what shows here can't drift from the app the way a mock-up would.
 *
 * The two sides talk over `postMessage`: the frame announces itself, this panel
 * answers with the API token and the current draft, and posts the draft again
 * on every edit. `view` (a `PREVIEW_VIEW` from `@congrs/schemas`) picks which
 * screen the frame shows, and `params` carries that screen's own options, such
 * as `{ cover }` for the covers screen. Both travel with the draft rather than
 * in the frame's address: a new address reloads the frame, and with it the
 * session, the event and every image. `views` (`{ label, value }` per screen)
 * adds a switcher; `controls` is a screen's own chrome, which belongs here
 * rather than inside the frame. Adding a screen:
 * `apps/mobile-next/components/preview/README.md`.
 */
const MobilePreview = ({
  eventId,
  config,
  view = PREVIEW_VIEW.home,
  params,
  views,
  controls,
  onViewChange,
  isDirty,
}) => {
  const frameRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  // Sent with the draft: the frame outlives the Features tab, and its own event
  // fetch would only pick up a toggled flag on its next refetch.
  const { data: flags } = useGetAllFeatureFlags({ eventId });
  const features = useMemo(
    () => flags?.filter((flag) => flag.enabled).map((flag) => flag.featureName),
    [flags]
  );
  const [hasTimedOut, setHasTimedOut] = useState(false);

  // The token is read on send rather than held in state: the ready handshake
  // can land at any point, and this is always the current session's.
  const post = useCallback((message) => {
    frameRef.current?.contentWindow?.postMessage(
      { source: PREVIEW_HOST_SOURCE, ...message },
      previewOrigin
    );
  }, []);

  // The ready handshake can land before or after any given edit, so the handler
  // needs the latest draft without re-subscribing on every keystroke.
  const draftRef = useRef({ config, features, view, params });
  draftRef.current = { config, features, view, params };

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== previewOrigin) return;
      if (event.data?.source !== PREVIEW_FRAME_SOURCE) return;
      if (event.data.type !== "ready") return;

      setIsReady(true);
      // Fresh on every handshake: the frame re-announces itself when the API
      // rejects the token it has, and access tokens are short-lived.
      getAccessToken().then((token) =>
        post({ type: "init", token, ...draftRef.current })
      );
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [post]);

  useEffect(() => {
    if (isReady) return;

    const id = setTimeout(() => setHasTimedOut(true), READY_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [isReady]);

  // What was last sent, so a re-render that rebuilt an identical draft doesn't
  // re-post it — the drafts carry picked images inline.
  const sentRef = useRef(null);

  useEffect(() => {
    if (!isReady) return;

    const id = setTimeout(() => {
      const payload = JSON.stringify({ config, features, view, params });
      if (payload === sentRef.current) return;

      sentRef.current = payload;
      post({ type: "config", config, features, view, params });
    }, CONFIG_DEBOUNCE_MS);

    return () => clearTimeout(id);
  }, [config, features, view, params, isReady, post]);

  return (
    // Sticky, so the phone stays in view while a long settings list scrolls.
    <Paper
      withBorder
      radius="md"
      w={PHONE_WIDTH * SCALE + 48}
      style={{ position: "sticky", top: 16 }}
    >
      <Stack gap={0}>
        <Group justify="space-between" px="md" py="sm">
          <Group gap={6}>
            <IconDeviceMobile size={16} />
            <Text size="xs" fw={700} tt="uppercase" c="dimmed">
              Live preview
            </Text>
          </Group>
          {isDirty && (
            <Badge
              size="sm"
              variant="light"
              color="orange"
              leftSection={
                <Box w={6} h={6} bg="orange" style={{ borderRadius: "50%" }} />
              }
            >
              Unsaved
            </Badge>
          )}
        </Group>

        <Divider />

        {(views?.length > 1 || controls) && (
          <Stack gap="xs" px="md" pt="md">
            {views?.length > 1 && (
              <SegmentedControl
                fullWidth
                size="xs"
                value={view}
                onChange={onViewChange}
                data={views}
              />
            )}
            {controls}
          </Stack>
        )}

        <Stack align="center" gap="xs" p="md">
          <Box
            p={6}
            style={{
              borderRadius: PHONE_CORNER_RADIUS * SCALE + 6,
              border: "1px solid var(--mantine-color-gray-4)",
              boxShadow: "var(--mantine-shadow-sm)",
            }}
          >
            <Box
              w={PHONE_WIDTH * SCALE}
              h={PHONE_HEIGHT * SCALE}
              style={{
                overflow: "hidden",
                borderRadius: PHONE_CORNER_RADIUS * SCALE,
              }}
            >
              <iframe
                ref={frameRef}
                title="Mobile app preview"
                src={`${webConfig.mobileWebUrl}/preview?eventId=${eventId}`}
                width={PHONE_WIDTH}
                height={PHONE_HEIGHT}
                style={{
                  border: 0,
                  transform: `scale(${SCALE})`,
                  transformOrigin: "top left",
                }}
              />
            </Box>
          </Box>

          {hasTimedOut && !isReady && (
            <Text size="xs" c="dimmed" ta="center">
              {webConfig.isLocal
                ? "The preview app isn't running. Start it with `pnpm --filter mobile-next web`."
                : "The preview app didn't respond. Reload the page to try again."}
            </Text>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default MobilePreview;
